/**
 * FileTypeIcon — единый source of truth для отображения типа файла в UI.
 *
 * Phase 3 (Files UX Refactor 2026-08-08): заменил локальные дубли `FileIcon`
 * в DatasetList.tsx и FilesResultsPage.tsx. Замена только в Files-модуле —
 * Search/Matching/Generation не трогаем (TZ Phase 3 Don't touch).
 *
 * Использование:
 *   <FileTypeIcon fileType={d.file_type} className="w-4 h-4" />
 *
 * Гарантии:
 * - aria-label по умолчанию = t('files.type.<fileType>') (i18n).
 *   Контекстные aria-label можно override'нуть через prop `ariaLabel`.
 * - Цвета соответствуют BACKLOG Phase 3:
 *   xlsx emerald, xls amber, csv orange, docx/doc blue, pdf rose.
 * - Если придёт неизвестный тип (forward-compat) — fallback на FileText
 *   neutral-500, без падения.
 * - role="img" — screen reader видит aria-label, иконка сама по себе
 *   не имеет семантики.
 */
import { FileSpreadsheet, FileText, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export type FileTypeKey = 'xlsx' | 'xls' | 'csv' | 'docx' | 'doc' | 'pdf';

export interface FileTypeIconProps {
  fileType: FileTypeKey | string;
  className?: string;
  /**
   * Override aria-label. По умолчанию — t('files.type.<fileType>').
   * Используй, если иконка в контексте отличается (например, в row-actions
   * совместно с текстовой подписью).
   */
  ariaLabel?: string;
}

interface IconVariant {
  Icon: LucideIcon;
  className: string; // tailwind color class
}

const VARIANTS: Record<string, IconVariant> = {
  xlsx: { Icon: FileSpreadsheet, className: 'text-success' },
  xls: { Icon: FileSpreadsheet, className: 'text-warning' },
  csv: { Icon: FileSpreadsheet, className: 'text-accent-orange' },
  docx: { Icon: FileText, className: 'text-info400' },
  doc: { Icon: FileText, className: 'text-info400' },
  pdf: { Icon: FileText, className: 'text-danger' },
};

const FALLBACK: IconVariant = {
  Icon: FileText,
  className: 'text-surface-500',
};

export function FileTypeIcon({
  fileType,
  className,
  ariaLabel,
}: FileTypeIconProps) {
  const { t } = useTranslation();
  const variant = VARIANTS[fileType] ?? FALLBACK;
  const { Icon, className: colorClass } = variant;
  const label =
    ariaLabel ?? t(`files.type.${fileType}`, { defaultValue: fileType });

  return (
    <Icon
      role="img"
      aria-label={label}
      className={cn('shrink-0', colorClass, className)}
    />
  );
}
